import React, { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

export const ParallaxLink = () => {
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x, { stiffness: 150, damping: 20 });
  const mouseYSpring = useSpring(y, { stiffness: 150, damping: 20 });

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["15deg", "-15deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-15deg", "15deg"]);

  const handleMouseMove = (e) => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;

    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
    setIsHovered(false);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className="relative flex justify-center perspective-1000"
      style={{ perspective: '1200px' }}
    >
      <motion.div
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
        className="relative w-40 h-40"
      >
        {/* Outer rotating ring - represents network */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 rounded-full"
          style={{
            border: '2px dashed rgba(16, 185, 129, 0.3)',
            transform: 'translateZ(-20px)'
          }}
        />

        {/* Main container with glow */}
        <motion.div
          animate={{
            boxShadow: [
              '0 0 30px rgba(16, 185, 129, 0.3)',
              '0 0 60px rgba(16, 185, 129, 0.6)',
              '0 0 30px rgba(16, 185, 129, 0.3)'
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-8 rounded-2xl glass flex items-center justify-center"
          style={{
            transform: 'translateZ(0px)',
          }}
        >
          {/* Background depth layers */}
          <motion.div
            className="absolute inset-0 rounded-2xl bg-primary/5"
            style={{ transform: 'translateZ(-15px)' }}
          />
          <motion.div
            className="absolute inset-2 rounded-xl bg-primary/10"
            style={{ transform: 'translateZ(-30px)' }}
          />

          {/* 3D XtreamCube Logo */}
          <div className="relative w-full h-full flex items-center justify-center">
            <motion.div
              style={{ transform: 'translateZ(50px)' }}
              animate={{
                rotateY: [0, 360],
                rotateX: isHovered ? 20 : 0,
              }}
              transition={{
                rotateY: { duration: 8, repeat: Infinity, ease: "linear" },
                rotateX: { duration: 0.3 }
              }}
              className="relative w-20 h-20"
            >
              {/* 3D Cube - Front Face */}
              <div 
                className="absolute inset-0 border-4 border-primary rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))',
                  boxShadow: '0 0 30px rgba(16, 185, 129, 0.5), inset 0 2px 8px rgba(255, 255, 255, 0.1)',
                  transform: 'translateZ(40px)',
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Corner dots */}
                <div className="absolute top-0 left-0 w-2 h-2 bg-primary rounded-full" style={{ boxShadow: '0 0 8px rgba(16, 185, 129, 1)' }} />
                <div className="absolute top-0 right-0 w-2 h-2 bg-primary rounded-full" style={{ boxShadow: '0 0 8px rgba(16, 185, 129, 1)' }} />
                <div className="absolute bottom-0 left-0 w-2 h-2 bg-primary rounded-full" style={{ boxShadow: '0 0 8px rgba(16, 185, 129, 1)' }} />
                <div className="absolute bottom-0 right-0 w-2 h-2 bg-primary rounded-full" style={{ boxShadow: '0 0 8px rgba(16, 185, 129, 1)' }} />
                
                {/* Center glow */}
                <motion.div
                  animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(16, 185, 129, 0.6), transparent)',
                    boxShadow: '0 0 20px rgba(16, 185, 129, 0.8)'
                  }}
                />
              </div>

              {/* Cube - Top Face */}
              <div 
                className="absolute inset-0 border-4 border-primary rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.08))',
                  boxShadow: '0 0 20px rgba(16, 185, 129, 0.4)',
                  transform: 'rotateX(90deg) translateZ(40px)',
                  transformStyle: 'preserve-3d'
                }}
              />

              {/* Cube - Right Face */}
              <div 
                className="absolute inset-0 border-4 border-primary rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(16, 185, 129, 0.06))',
                  boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)',
                  transform: 'rotateY(90deg) translateZ(40px)',
                  transformStyle: 'preserve-3d'
                }}
              />

              {/* Cube - Back Face */}
              <div 
                className="absolute inset-0 border-4 border-primary/50 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.03))',
                  transform: 'translateZ(-40px)',
                  transformStyle: 'preserve-3d'
                }}
              />

              {/* Cube - Left Face */}
              <div 
                className="absolute inset-0 border-4 border-primary/50 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.03))',
                  transform: 'rotateY(-90deg) translateZ(40px)',
                  transformStyle: 'preserve-3d'
                }}
              />

              {/* Cube - Bottom Face */}
              <div 
                className="absolute inset-0 border-4 border-primary/50 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.03))',
                  transform: 'rotateX(-90deg) translateZ(40px)',
                  transformStyle: 'preserve-3d'
                }}
              />
            </motion.div>

            {/* Energy particles around cube */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 bg-primary rounded-full"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: `translateZ(${40}px)`,
                  boxShadow: '0 0 8px rgba(16, 185, 129, 0.8)'
                }}
                animate={{
                  x: [0, Math.cos((i * Math.PI * 2) / 8) * 45],
                  y: [0, Math.sin((i * Math.PI * 2) / 8) * 45],
                  scale: [0, 1, 0],
                  opacity: [0, 0.8, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.3,
                  ease: "easeOut"
                }}
              />
            ))}
          </div>

          {/* Orbiting connection indicators */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={`orbit-${i}`}
              className="absolute w-3 h-3 bg-primary/60 rounded-full"
              style={{
                left: '50%',
                top: '50%',
                transform: `translateZ(${20}px)`,
              }}
              animate={{
                x: [0, Math.cos((i * Math.PI * 2) / 3) * 40],
                y: [0, Math.sin((i * Math.PI * 2) / 3) * 40],
                scale: [0, 1, 0],
                opacity: [0, 0.8, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 1,
                ease: "easeOut"
              }}
            />
          ))}
        </motion.div>

        {/* Text labels for context */}
        <motion.div
          style={{ transform: 'translateZ(20px)' }}
          className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-primary text-xs font-mono opacity-60 whitespace-nowrap"
        >
          DOMAIN GATEWAY
        </motion.div>

        {/* Corner decorative elements */}
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          className="absolute inset-4 rounded-xl"
          style={{
            border: '1px solid rgba(16, 185, 129, 0.2)',
            transform: 'translateZ(-10px)'
          }}
        />
      </motion.div>
    </div>
  );
};